import { Button, Heading } from 'lucid-components-react';
import React, { useContext } from 'react';
import ReactHtmlParser from 'react-html-parser';

import LayoutContext from '../../contexts/layoutContext';
import {
  Drupal_FieldNodeEduFieldBackNForthSection,
  Drupal_FieldNodeEnterpriseFieldBackNForthSection,
  Drupal_FieldNodePartnersFieldBackNForthSection,
  Drupal_ParagraphBackNForthSection,
  Drupal_ParagraphCtaButton,
  Drupal_ParagraphMarketoForm,
  Drupal_ParagraphSeoImage
} from '../../generated/graphql';
import { getProcessedValue } from '../../utils/helpers';
import linkParser from '../../utils/link-parser';
import Link from '../link/link';
import './back-n-forth.scss';
import useMarketoLink from '../../hooks/use-marketo-link';

interface CustomJumplinks {
  text: string;
  search: string;
}

interface BackNForthProps {
  sections:
    | Drupal_FieldNodePartnersFieldBackNForthSection[]
    | Drupal_FieldNodeEnterpriseFieldBackNForthSection[]
    | Drupal_FieldNodeEduFieldBackNForthSection[]
    | null
    | undefined;
  useJumplinks?: boolean | null;
  customJumplinks?: CustomJumplinks[];
  largeLinks?: boolean;
}

enum BLOCK_OPTIONS {
  DEFAULT = 'nearest',
  START = 'start',
  CENTER = 'center',
  END = 'end'
}

const BackNForth = ({
  sections,
  useJumplinks,
  customJumplinks,
  largeLinks
}: BackNForthProps) => {
  const scrollTo = (selector: string, block: BLOCK_OPTIONS) => {
    const el = document.querySelector(selector);
    el!.scrollIntoView({
      behavior: 'smooth',
      block,
      inline: 'center'
    });
  };

  const { language } = useContext(LayoutContext);

  const getMarketoLink = useMarketoLink(language);

  return (
    <section className="back-n-forth">
      {useJumplinks && (
        <div className="back-n-forth__nav">
          {sections!.map((section, i: number) => {
            const sectionEntity = section.entity as Drupal_ParagraphBackNForthSection;
            return (
              <Button
                asLink
                underline="none"
                className={`back-n-forth__button ${
                  largeLinks ? 'back-n-forth__button--large' : ''
                }`}
                key={sectionEntity.fieldTitle!}
                onClick={() =>
                  scrollTo(`.bnf-section-${i}`, BLOCK_OPTIONS.CENTER)
                }
              >
                {sectionEntity.fieldTitle}
              </Button>
            );
          })}
          {customJumplinks &&
            customJumplinks.map(custom => (
              <button
                type="button"
                className={`back-n-forth__button ${
                  largeLinks ? 'back-n-forth__button--large' : ''
                }`}
                key={custom.text}
                onClick={() => scrollTo(custom.search, BLOCK_OPTIONS.START)}
              >
                {custom.text}
              </button>
            ))}
        </div>
      )}

      {sections!.map((section, i: number) => {
        const sectionEntity = section.entity as Drupal_ParagraphBackNForthSection;
        const sectionImage = sectionEntity.fieldSectionImage!
          .entity as Drupal_ParagraphSeoImage;
        return (
          <article
            key={sectionEntity.fieldTitle!}
            className={`back-n-forth__item bnf-section-${i}`}
          >
            <figure>
              <img
                src={sectionImage.fieldImageLink!}
                alt={sectionImage.fieldAltText!}
              />
            </figure>
            <div className="back-n-forth__text">
              <Heading as="h2" variant="subheadS" mt={0}>
                {sectionEntity.fieldTitle}
              </Heading>
              <div>
                {ReactHtmlParser(
                  getProcessedValue(sectionEntity.fieldSectionDescription),
                  {
                    transform: linkParser
                  }
                )}
              </div>
              {sectionEntity.fieldCtaButton &&
                sectionEntity.fieldCtaButton.length > 1 &&
                sectionEntity.fieldCtaButton[0]!.entity &&
                sectionEntity.fieldCtaButton.map((cta, j) => {
                  const button = cta!.entity as Drupal_ParagraphCtaButton;
                  const buttonMarketo = button.fieldMarketo!
                    .entity as Drupal_ParagraphMarketoForm;
                  const { url, copy } = getMarketoLink(
                    buttonMarketo.fieldMarketoId
                  );
                  return button.fieldButtonLink ? (
                    <Link
                      primary={!button.fieldIsSecondary}
                      secondary={!!button.fieldIsSecondary}
                      asButton={!button.fieldIsSecondary}
                      key={button.fieldButtonText!}
                      to={button.fieldButtonLink}
                      className={`${j === 1 ? 'pad--left' : ''}`}
                    >
                      {button.fieldButtonText}
                    </Link>
                  ) : (
                    <Link
                      to={url}
                      asButton={!button.fieldIsSecondary}
                      primary
                      key={button.fieldButtonText!}
                      className={`${j === 1 ? 'pad--left' : ''}`}
                    >
                      {button.fieldButtonText || copy}
                    </Link>
                  );
                })}
            </div>
          </article>
        );
      })}
    </section>
  );
};

export default BackNForth;
